<script setup lang="ts">
import { HgCollapsible, HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, GeoLocationType } from '@smg-real-estate/geo-libs';
import { computed, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RouteLocationRaw } from 'vue-router';

import { isServer } from '@/helpers/Platform/platform';
import { getAgentHomeRouteName, getHomeRouteName } from '@/helpers/Url/Url';
import { useLocale } from '@/router/routeMeta';
import { renderGeoLocationName, useGeoStore } from '@/stores/geo';
import type { DirectoryType } from '@/types/directoryType';

const SWISS_COUNTRY_LOCATION_ID = 'geo-country-switzerland';
const EXCLUDED_CHILD_LOCATION_TYPES = new Set<GeoLocationType>([
  'cityregion',
  'citydistrict',
  'zip',
  'ziparea',
]);

const CHILD_TITLE_TYPES = new Set<GeoLocationType>([
  'country',
  'canton',
  'district',
  'region',
  'municipality',
  'place',
]);

const HOME_ROUTE_NAME_BY_DIRECTORY_TYPE: Record<
  DirectoryType,
  (language: HgSupportedLanguage) => string
> = {
  agency: getHomeRouteName,
  agent: getAgentHomeRouteName,
};

interface SeoLink {
  id: string;
  name: string;
  route: RouteLocationRaw;
}

const { directoryType, searchedLocations } = defineProps<{
  directoryType: DirectoryType;
  searchedLocations?: GeoLocation[];
}>();

const { t } = useI18n();
const language = useLocale();
const geoStore = useGeoStore();

const childLocations = ref<GeoLocation[]>([]);

const isSeoChildLocation = (location: GeoLocation | undefined): location is GeoLocation =>
  !!location && !EXCLUDED_CHILD_LOCATION_TYPES.has(location.type);

const primaryLocationId = computed(() => {
  const primaryLocation = searchedLocations?.[0];
  return primaryLocation?.id ?? SWISS_COUNTRY_LOCATION_ID;
});

const parentLocation = computed(() => geoStore.getGeoLocationById(primaryLocationId.value));

const getLocalizedUrlName = (location: GeoLocation): string =>
  location.urlNames[language.value] ?? location.urlNames[HgSupportedLanguage.DE];

const seoLinks = computed<SeoLink[]>(() =>
  childLocations.value.map((location) => ({
    id: location.newId ?? location.id,
    name: renderGeoLocationName(location, language.value),
    route: {
      name: HOME_ROUTE_NAME_BY_DIRECTORY_TYPE[directoryType](language.value),
      params: {
        location: getLocalizedUrlName(location),
      },
    },
  })),
);

const childTitleKey = computed(() => {
  const childType = childLocations.value[0]?.type;

  return childType && CHILD_TITLE_TYPES.has(childType)
    ? `agencyDirectorySeoLinks.childTitle.${childType}`
    : 'agencyDirectorySeoLinks.childTitle.default';
});

const collapsibleTitle = computed(() => {
  const location = parentLocation.value;

  if (!location) {
    return '';
  }

  return t(childTitleKey.value, {
    location: renderGeoLocationName(location, language.value),
  });
});

const resolveSeoLocations = async () => {
  await geoStore.resolveLocationsById([primaryLocationId.value]);

  const location = geoStore.getGeoLocationById(primaryLocationId.value);
  const childIds = location?.children ?? [];

  if (childIds.length === 0) {
    childLocations.value = [];
    return;
  }

  await geoStore.resolveLocationsById(childIds);

  childLocations.value =
    childIds.map((childId) => geoStore.getGeoLocationById(childId)).filter(isSeoChildLocation) ??
    [];
};

onServerPrefetch(resolveSeoLocations);
watch(primaryLocationId, resolveSeoLocations, { immediate: !isServer() });
</script>

<template>
  <article
    v-if="seoLinks.length > 0"
    :class="$style.container"
    :data-test="`${directoryType}-directory-seo-links`"
  >
    <h2 :class="$style.title">{{ t('agencyDirectorySeoLinks.title') }}</h2>

    <HgCollapsible
      :doExpandTransition="false"
      :triggerCssClass="$style.listTrigger"
      :triggerCssClassFolded="$style.listTriggerFolded"
      :bodyCssClassUnfolded="$style.listBodyUnfolded"
    >
      <template #header>
        <div :class="$style.listTitle">{{ collapsibleTitle }}</div>
      </template>

      <ul :class="$style.list">
        <li v-for="link in seoLinks" :key="link.id" :class="$style.listItem">
          <router-link :to="link.route">
            {{ link.name }}
          </router-link>
        </li>
      </ul>
    </HgCollapsible>
  </article>
</template>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  margin-top: $space-250;
  margin-bottom: $space-200;
}

.title {
  margin: 0 0 $space-100;

  @include heading-2;
}

.listTrigger {
  position: relative;
}

.listTriggerFolded::after {
  position: absolute;
  top: 100%;
  width: 100%;
  content: '';
  border-bottom: 1px solid $color-grey-50;
}

.listBodyUnfolded {
  padding: $space-50 0;
  border-bottom: 1px solid $color-grey-70;
}

.listTitle {
  padding: $space-75 0;
  color: $color-grey-100;

  @include heading-4;
}

.list {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: $space-50;
  padding: 0 0 $space-50;
  margin: 0;
  list-style-type: none;

  @include body-2;

  @media (min-width: $viewport-md) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: $space-200;
  }
}

.listItem {
  position: relative;
  padding-left: $space-50;
}

.listItem::before {
  position: absolute;
  left: 0;
  content: '–';
}
</style>
